// useCityDirectory.js
// Landing-page-only hook: suggestion data sourced from cities that actually
// have a servicing store (store.city -> static_cities.id) - the default
// "popular cities" list (top N) and search-as-you-type matches. Backed by
// the same cityService redux slice useCityServiceCheck uses.
//
// Everything this hook returns is already store-backed, so anything the
// user picks from it (popular list or search suggestions) is guaranteed
// serviceable - no separate check needed. Only auto-detect (an arbitrary
// geolocation result) needs useCityServiceCheck's real API check.
import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTopServiceableCities,
  searchServiceableCities,
  clearCitySearchResults,
} from "../store/citySlices/cityServiceSlice";

export default function useCityDirectory({ topLimit = 5, searchLimit = 10, debounceMs = 300 } = {}) {
  const dispatch = useDispatch();
  const topCities = useSelector((state) => state.cityService.topCities);
  const topCitiesLoading = useSelector((state) => state.cityService.topCitiesLoading);
  const searchResults = useSelector((state) => state.cityService.searchResults);
  const searching = useSelector((state) => state.cityService.searching);

  const debounceRef = useRef(null);

  // Load the default "cities we serve" list once, on mount.
  useEffect(() => {
    dispatch(fetchTopServiceableCities(topLimit));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced so every keystroke doesn't hit the backend.
  const searchCities = useCallback(
    (keyword) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!keyword?.trim()) {
        dispatch(clearCitySearchResults());
        return;
      }

      debounceRef.current = setTimeout(() => {
        dispatch(searchServiceableCities({ keyword: keyword.trim(), limit: searchLimit }));
      }, debounceMs);
    },
    [dispatch, searchLimit, debounceMs]
  );

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  return { topCities, topCitiesLoading, searchResults, searching, searchCities };
}